import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import * as Yup from 'yup';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomButton from '@ui-components/Button/CustomButton';
import BackIcon from '@assets/images/BackIcon.svg';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CustomSelect from '@ui-components/Select/CustomSelect';
import CustomSwitch from '@qler-view/shared_lib/src/shared/lib/ui-components/Switch/CustomSwitch';
import {
  OPSSTAFF_ROUTE_PATH,
  VIEW_OPSSTAFF_ROUTE_PATH,
} from '../../routers/routePaths';
import {
  useGetOpsStaffMetaData,
  useSaveOrUpdateOpsStaff,
  useGetOpsStaffDetailsById,
} from '../../API/hooks/opsStaffHook';
import { Form, Formik } from 'formik';
import type { OpsStaffData } from '../../API/services/opsStaffService';
import ConfirmDiscardDialog from '../components/ConfirmDiscardDialog';

interface NewOpsStaffRouteState {
  firstName?: string;
  lastName?: string;
  email?: string;
  isOpsList?: boolean;
}

const opsStaffInitialValues = {
  id: 0,
  userBusinessId: '',
  firstName: '',
  lastName: '',
  primaryEmail: '',
  roleId: 0,
  status: true,
  notes: '',
};
const AddNewOpsStaff: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId') ?? '0';
  const { opsStaffId } = useParams<{ opsStaffId?: string }>();
  const { OpsStaffMetaData } = useGetOpsStaffMetaData(userId);
  const location = useLocation() as {
    state?: NewOpsStaffRouteState;
  };
  const { firstName, lastName, email } = location.state || {};
  const [opsStaffSubmitAction, setOpsStaffSubmitAction] = useState<string>('');

  const [openOpsStaffConfirmDialog, setOpenOpsStaffConfirmDialog] =
    useState(false);

  const handleOpenOpsStaffConfirmDialog = (state: boolean) => {
    setOpenOpsStaffConfirmDialog(state);
  };

  const [initialOpsStaffValues, setInitialOpsStaffValues] =
    useState<OpsStaffData>({
      ...opsStaffInitialValues,
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      primaryEmail: email ?? '',
    });

  useEffect(() => {
    if (OpsStaffMetaData?.roles?.length) {
      const opsStaff = OpsStaffMetaData.roles.find(
        (role) => role.name === 'Ops-Staff'
      );

      if (opsStaff) {
        setInitialOpsStaffValues({
          ...initialOpsStaffValues,
          roleId: opsStaff.id,
        });
      }
    }
  }, [OpsStaffMetaData]);

  const onSuccess = () => {
    if (opsStaffId) {
      navigate(`${OPSSTAFF_ROUTE_PATH}?toast=update`);
    } else {
      navigate(`${OPSSTAFF_ROUTE_PATH}?toast=success`);
    }
  };

  const { mutate: saveOrUpdateOpsStaff } = useSaveOrUpdateOpsStaff(onSuccess);

  const handleOpsStaffSaveOrUpdate = async (
    opsStaffData: typeof opsStaffInitialValues
  ) => {
    const { ...payload } = opsStaffData;

    if (opsStaffSubmitAction) {
      saveOrUpdateOpsStaff({
        userId,
        action: opsStaffSubmitAction,
        ...payload,
      });
    }
  };

  const { OpsStaffDetailsById, isLoading } = useGetOpsStaffDetailsById(
    userId,
    Number(opsStaffId)
  );

  useEffect(() => {
    if (OpsStaffDetailsById) {
      setInitialOpsStaffValues({
        id: OpsStaffDetailsById.id,
        userBusinessId: OpsStaffDetailsById.userBusinessId,
        firstName: OpsStaffDetailsById.firstName,
        lastName: OpsStaffDetailsById.lastName,
        primaryEmail: OpsStaffDetailsById.primaryEmail,
        roleId: OpsStaffDetailsById.roleId,
        status: OpsStaffDetailsById.status,
        notes: OpsStaffDetailsById.notes,
      });
    }
  }, [OpsStaffDetailsById]);

  const opsStaffValidationSchema = Yup.object({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    primaryEmail: Yup.string()
      .required('Primary Email is required')
      .email('Invalid email'),
    roleId: Yup.number()
      .required('Role is required')
      .min(1, 'Role is required'),
    notes: Yup.string().max(
      500,
      'Notes must be no more than 500 characters long.'
    ),
  });

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  const handleOpsBackButtonClick = () => {
    if (opsStaffId && !location.state?.isOpsList) {
      navigate(VIEW_OPSSTAFF_ROUTE_PATH + `/${opsStaffId}`, { replace: true });
    } else {
      navigate(OPSSTAFF_ROUTE_PATH, { replace: true });
    }
  };

  return (
    <Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="text-align-left display-flex">
              <CustomIconButton
                id="back-button-ops-staff"
                icon={<BackIcon />}
                onClick={() => {
                  handleOpsBackButtonClick();
                }}
              ></CustomIconButton>
              <CustomTypography
                className="pl-8"
                variant="h4"
                id="add-new-ops-staff-title"
              >
                {opsStaffId ? 'Edit Ops User' : 'Add New Ops User'}
              </CustomTypography>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Formik
        initialValues={initialOpsStaffValues}
        validateOnMount={false}
        validationSchema={opsStaffValidationSchema}
        onSubmit={(values) => {
          handleOpsStaffSaveOrUpdate(values);
        }}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          // isValid,
          // dirty,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form>
            <Box className="form-background p-24">
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-32"
                    id="ops-staff-info-title"
                  >
                    Ops User Information
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Box className="display-flex">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="ops-staff-first-name-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      First Name&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomTextField
                      id="txt-ops-staff-first-name"
                      name="firstName"
                      placeholder="Type First Name"
                      size="small"
                      value={values.firstName}
                      disabled={Boolean(firstName ?? values.firstName)}
                      onChange={(event) => {
                        setFieldValue('firstName', event.target.value);
                      }}
                    />
                    {errors?.firstName && touched?.firstName && (
                      <CustomTypography
                        id="validate-ops-staff-firstname"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.firstName}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="ops-staff-last-name-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Last Name&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomTextField
                      id="txt-ops-staff-last-name"
                      name="lastName"
                      placeholder="Type Last Name"
                      size="small"
                      value={values.lastName}
                      disabled={Boolean(lastName ?? values.lastName)}
                      onChange={(event) => {
                        setFieldValue('lastName', event.target.value);
                      }}
                    />
                    {errors?.lastName && touched?.lastName && (
                      <CustomTypography
                        id="validate-ops-staff-lastname"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.lastName}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="ops-staff-primary-email-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Primary Email&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomTextField
                      id="txt-ops-staff-primary-email"
                      name="primaryEmail"
                      placeholder="Type Primary Email"
                      size="small"
                      value={values.primaryEmail}
                      disabled={Boolean(email ?? values.primaryEmail)}
                      onChange={(event) => {
                        setFieldValue('primaryEmail', event.target.value);
                      }}
                    />
                    {errors?.primaryEmail && touched?.primaryEmail && (
                      <CustomTypography
                        id="validate-ops-staff-primaryemail"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.primaryEmail}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="ops-staff-role-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Role&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomSelect
                      id="ops-staff-user-role"
                      placeholder="Select Role"
                      size="small"
                      options={OpsStaffMetaData?.roles ?? []}
                      onChange={(value) => {
                        setFieldValue('roleId', Number(value));
                      }}
                      selectedValue={values.roleId}
                      fullWidth
                    />
                    {errors?.roleId && touched?.roleId && (
                      <CustomTypography
                        id="validate-ops-staff-role"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.roleId}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="ops-staff-user-status-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Status
                    </CustomTypography>
                    <Box className="text-align-left">
                      <CustomSwitch
                        id="facility-user-status-switch"
                        checked={values.status}
                        onChange={(event) => {
                          setFieldValue('status', event.target.checked);
                        }}
                        labelPlacement="end"
                        label={values.status ? 'Active' : 'Inactive'}
                      ></CustomSwitch>
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="staff-notes-label"
                    >
                      Ops User Notes
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      id="notes-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Notes
                    </CustomTypography>
                    <CustomTextField
                      id="txt-ops-staff-notes"
                      placeholder="Type Notes"
                      size="small"
                      multiLine
                      rows={4}
                      value={values.notes}
                      onChange={(event) => {
                        setFieldValue('notes', event.target.value);
                      }}
                    ></CustomTextField>
                    {errors.notes && (
                      <CustomTypography
                        id="validate-ops-staff-notes"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.notes}
                      </CustomTypography>
                    )}
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <Grid2 container>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Box className="display-flex justify-end">
                      <Box className="pr-12">
                        <CustomButton
                          id="cancel-button"
                          onClick={() => {
                            handleOpenOpsStaffConfirmDialog(true);
                          }}
                          variant="outlined"
                        >
                          Cancel
                        </CustomButton>
                      </Box>
                      <Box>
                        <CustomButton
                          id="save-button"
                          onClick={() => {
                            setOpsStaffSubmitAction(
                              opsStaffId ? 'Update' : 'Save'
                            );
                            handleSubmit();
                          }}
                          variant="contained"
                        >
                          {opsStaffId ? 'Update' : 'Save'}
                        </CustomButton>
                      </Box>
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
      <Box>
        <ConfirmDiscardDialog
          open={openOpsStaffConfirmDialog}
          onClose={() => handleOpenOpsStaffConfirmDialog(false)}
          navigateTo={
            opsStaffId && !location.state?.isOpsList
              ? `${VIEW_OPSSTAFF_ROUTE_PATH}/${opsStaffId}`
              : OPSSTAFF_ROUTE_PATH
          }
        />
      </Box>
    </Box>
  );
};

export default AddNewOpsStaff;
